import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Package, Plus, Search, AlertTriangle, CheckCircle2,
  MapPin, User, DollarSign, ArrowRight, ArrowLeftRight, ShoppingCart, Wrench, XCircle, Eye,
  Truck, Clock, Camera, QrCode, Navigation, FileText, UtensilsCrossed, Box
} from 'lucide-react';

const LogisticsRequests = ({ userRole = 'manager' }) => {
  const [activeFilter, setActiveFilter] = useState(userRole === 'driver' ? 'assigned' : 'all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Expanded request types - can be anything
  const requestTypes = [
    { id: 'warehouse-to-warehouse', label: 'Warehouse → Warehouse', icon: ArrowLeftRight, color: 'blue', category: 'Transfer' },
    { id: 'warehouse-to-store', label: 'Warehouse → Store', icon: ShoppingCart, color: 'green', category: 'Replenishment' },
    { id: 'warehouse-to-client', label: 'Warehouse → Client', icon: User, color: 'orange', category: 'Delivery' },
    { id: 'equipment-move', label: 'Equipment Move', icon: Wrench, color: 'purple', category: 'Equipment' },
    { id: 'central-kitchen', label: 'Central Kitchen → Store', icon: UtensilsCrossed, color: 'red', category: 'Food Products' },
    { id: 'finished-products', label: 'Finished Products', icon: Box, color: 'indigo', category: 'Products' },
  ];

  // Mock data - expanded to include various types
  const allRequests = [
    {
      id: 'LOG-2024-001',
      type: 'warehouse-to-store',
      priority: 'emergency',
      status: 'pending',
      from: { type: 'warehouse', name: 'Main Warehouse', location: 'Riyadh' },
      to: { type: 'store', name: 'Store #45', location: 'Jeddah' },
      items: [
        { sku: 'ELEC-001', name: 'Laptop Dell XPS', quantity: 25, weight: 500, type: 'inventory' },
        { sku: 'OFF-123', name: 'Printer HP', quantity: 10, weight: 200, type: 'inventory' }
      ],
      totalWeight: 14500,
      totalVolume: 12.5,
      requestedBy: 'Store Manager - Jeddah',
      requestedByUserId: 'user-001',
      requestedDate: '2024-01-28 10:30 AM',
      requiredDate: '2024-01-29',
      estimatedCost: 2500,
      requiresApproval: true,
      approvalStatus: 'pending',
      approvedBy: null,
      description: 'Urgent replenishment for weekend sale event'
    },
    {
      id: 'LOG-2024-002',
      type: 'warehouse-to-client',
      priority: 'high',
      status: 'approved',
      from: { type: 'warehouse', name: 'Distribution Center A', location: 'Jeddah' },
      to: { type: 'client', name: 'ABC Corporation', location: 'Riyadh' },
      items: [
        { sku: 'FURN-045', name: 'Office Chair', quantity: 50, weight: 2500, type: 'inventory' }
      ],
      totalWeight: 12500,
      totalVolume: 15.0,
      requestedBy: 'Sales Department',
      requestedByUserId: 'user-002',
      requestedDate: '2024-01-27 2:15 PM',
      requiredDate: '2024-01-30',
      estimatedCost: 3200,
      requiresApproval: true,
      approvalStatus: 'approved',
      approvedBy: 'Ahmed Al-Rashid',
      approvedDate: '2024-01-27 4:30 PM',
      description: 'Client delivery for new office setup'
    },
    {
      id: 'LOG-2024-003',
      type: 'warehouse-to-warehouse',
      priority: 'medium',
      status: 'in-progress',
      from: { type: 'warehouse', name: 'Main Warehouse', location: 'Riyadh' },
      to: { type: 'warehouse', name: 'Distribution Center A', location: 'Jeddah' },
      items: [
        { sku: 'RAW-234', name: 'Steel Sheets', quantity: 200, weight: 10000, type: 'inventory' }
      ],
      totalWeight: 10000,
      totalVolume: 8.0,
      requestedBy: 'Warehouse Manager',
      requestedByUserId: 'user-003',
      requestedDate: '2024-01-26 9:00 AM',
      requiredDate: '2024-01-28',
      estimatedCost: 1800,
      requiresApproval: false,
      approvalStatus: 'approved',
      assignedVehicle: 'VH-001',
      assignedDriver: 'Ahmed Al-Rashid',
      assignedDriverId: 'driver-001',
      startedAt: '2024-01-28 8:00 AM',
      description: 'Stock transfer between warehouses'
    },
    {
      id: 'LOG-2024-004',
      type: 'equipment-move',
      priority: 'low',
      status: 'completed',
      from: { type: 'location', name: 'Office Building A', location: 'Riyadh' },
      to: { type: 'location', name: 'Office Building B', location: 'Riyadh' },
      items: [
        { sku: 'EQ-001', name: 'Office Desks', quantity: 20, weight: 2000, type: 'equipment' },
        { sku: 'EQ-002', name: 'Conference Tables', quantity: 5, weight: 500, type: 'equipment' }
      ],
      totalWeight: 2500,
      totalVolume: 5.0,
      requestedBy: 'Facilities Manager',
      requestedByUserId: 'user-004',
      requestedDate: '2024-01-25 11:00 AM',
      requiredDate: '2024-01-27',
      estimatedCost: 800,
      requiresApproval: false,
      approvalStatus: 'approved',
      assignedVehicle: 'VH-002',
      assignedDriver: 'Mohammed Ali',
      assignedDriverId: 'driver-002',
      completedAt: '2024-01-27 3:45 PM',
      description: 'Office relocation equipment move'
    },
    {
      id: 'LOG-2024-006',
      type: 'central-kitchen',
      priority: 'high',
      status: 'in-progress',
      from: { type: 'kitchen', name: 'Central Kitchen', location: 'Riyadh' },
      to: { type: 'store', name: 'Store #12', location: 'Dammam' },
      items: [
        { sku: 'FOOD-001', name: 'Prepared Meals', quantity: 150, weight: 300, type: 'finished-product', temperature: '2-8°C' },
        { sku: 'FOOD-002', name: 'Baked Goods', quantity: 200, weight: 400, type: 'finished-product', temperature: 'Ambient' }
      ],
      totalWeight: 700,
      totalVolume: 3.5,
      requestedBy: 'Store Manager - Dammam',
      requestedByUserId: 'user-005',
      requestedDate: '2024-01-28 6:00 AM',
      requiredDate: '2024-01-28',
      estimatedCost: 1200,
      requiresApproval: false,
      approvalStatus: 'approved',
      assignedVehicle: 'VH-003',
      assignedDriver: 'Omar Hassan',
      assignedDriverId: 'driver-003',
      startedAt: '2024-01-28 7:00 AM',
      description: 'Daily food delivery from central kitchen',
      specialRequirements: 'Temperature controlled vehicle required'
    },
    {
      id: 'LOG-2024-007',
      type: 'finished-products',
      priority: 'medium',
      status: 'approved',
      from: { type: 'warehouse', name: 'Production Facility', location: 'Riyadh' },
      to: { type: 'store', name: 'Store #45', location: 'Jeddah' },
      items: [
        { sku: 'PROD-089', name: 'Custom Furniture Set', quantity: 10, weight: 5000, type: 'finished-product' }
      ],
      totalWeight: 50000,
      totalVolume: 25.0,
      requestedBy: 'Production Manager',
      requestedByUserId: 'user-006',
      requestedDate: '2024-01-27 3:00 PM',
      requiredDate: '2024-01-30',
      estimatedCost: 3500,
      requiresApproval: true,
      approvalStatus: 'approved',
      approvedBy: 'Logistics Manager',
      approvedDate: '2024-01-27 5:00 PM',
      description: 'Finished products ready for delivery'
    }
  ];

  // Filter requests based on user role
  const getFilteredRequests = () => {
    let requests = allRequests;

    // User view: Only show their own requests
    if (userRole === 'user') {
      requests = requests.filter(req => req.requestedByUserId === 'user-001'); // In production, use actual user ID
    }

    // Driver view: Only show assigned tasks
    if (userRole === 'driver') {
      requests = requests.filter(req => 
        req.assignedDriverId === 'driver-001' && // In production, use actual driver ID
        ['in-progress', 'assigned'].includes(req.status)
      );
    }

    // Apply status filter
    if (activeFilter !== 'all' && activeFilter !== 'assigned') {
      requests = requests.filter(req => req.status === activeFilter);
    }

    // Apply search filter
    if (searchQuery) {
      requests = requests.filter(req =>
        req.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.from.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.to.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return requests;
  };

  const filteredRequests = getFilteredRequests();

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'emergency': return 'bg-red-100 text-red-700 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'approved': return 'bg-purple-100 text-purple-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRequestTypeIcon = (type) => {
    return requestTypes.find(t => t.id === type)?.icon || Package;
  };

  const handleApprove = (requestId) => {
    alert(`Approving request ${requestId}. This would update the request status and notify the requester.`);
  };

  const handleReject = (requestId) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      alert(`Request ${requestId} rejected. Reason: ${reason}`);
    }
  };

  const handleAssign = (requestId) => {
    alert(`Assigning request ${requestId} to vehicle and driver. This would open assignment interface.`);
  };

  const handleScanItem = (requestId, itemSku) => {
    alert(`Scanning item ${itemSku} for request ${requestId}. This would open barcode scanner.`);
  };

  const handleUpdateStatus = (requestId, newStatus) => {
    alert(`Updating request ${requestId} status to ${newStatus}. This would update the system.`);
  };

  const handleCaptureEPOD = (requestId) => {
    alert(`Capturing ePOD for request ${requestId}. This would open ePOD capture interface.`);
  };

  // Get filters based on role
  const getFilters = () => {
    if (userRole === 'driver') {
      return [
        { id: 'assigned', label: 'My Tasks', count: filteredRequests.length },
        { id: 'in-progress', label: 'In Progress', count: allRequests.filter(r => r.status === 'in-progress' && r.assignedDriverId === 'driver-001').length },
      ];
    }
    if (userRole === 'user') {
      return [
        { id: 'all', label: 'My Requests', count: allRequests.filter(r => r.requestedByUserId === 'user-001').length },
        { id: 'pending', label: 'Pending', count: allRequests.filter(r => r.status === 'pending' && r.requestedByUserId === 'user-001').length },
        { id: 'approved', label: 'Approved', count: allRequests.filter(r => r.status === 'approved' && r.requestedByUserId === 'user-001').length },
        { id: 'in-progress', label: 'In Progress', count: allRequests.filter(r => r.status === 'in-progress' && r.requestedByUserId === 'user-001').length },
        { id: 'completed', label: 'Completed', count: allRequests.filter(r => r.status === 'completed' && r.requestedByUserId === 'user-001').length },
      ];
    }
    // Manager view
    return [
      { id: 'all', label: 'All Requests', count: allRequests.length },
      { id: 'pending', label: 'Pending', count: allRequests.filter(r => r.status === 'pending').length },
      { id: 'approved', label: 'Approved', count: allRequests.filter(r => r.status === 'approved').length },
      { id: 'in-progress', label: 'In Progress', count: allRequests.filter(r => r.status === 'in-progress').length },
      { id: 'completed', label: 'Completed', count: allRequests.filter(r => r.status === 'completed').length },
    ];
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {userRole === 'user' && 'My Logistics Requests'}
              {userRole === 'driver' && 'My Assigned Tasks'}
              {userRole === 'manager' && 'Unified Logistics Request System'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {userRole === 'user' && 'Submit and track your logistics requests'}
              {userRole === 'driver' && 'View your assigned tasks and update delivery status'}
              {userRole === 'manager' && 'Create and manage logistics movement requests from any department'}
            </p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            {(userRole === 'user' || userRole === 'manager') && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                New Request
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          {getFilters().map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === filter.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      {/* Request Types Quick Actions - Only for User and Manager */}
      {(userRole === 'user' || userRole === 'manager') && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {requestTypes.map((type) => {
            const Icon = type.icon;
            return (
              <motion.button
                key={type.id}
                onClick={() => setShowCreateModal(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`p-4 bg-gradient-to-br from-${type.color}-50 to-${type.color}-100 rounded-xl border-2 border-${type.color}-200 hover:border-${type.color}-300 transition-all text-left`}
              >
                <div className={`p-2 rounded-lg bg-${type.color}-500 w-fit mb-2`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{type.label}</h3>
                <p className="text-xs text-gray-600">{type.category}</p>
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Driver View - Task List */}
      {userRole === 'driver' && filteredRequests.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Assigned Tasks</h3>
          <p className="text-gray-600">You don't have any assigned tasks at the moment.</p>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.map((request) => {
          const TypeIcon = getRequestTypeIcon(request.type);
          return (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`p-3 rounded-lg bg-blue-100`}>
                    <TypeIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{request.id}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(request.priority)}`}>
                        {request.priority.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                        {request.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span className="font-medium">{request.from.name}</span>
                        <ArrowRight className="w-4 h-4 mx-1" />
                        <span className="font-medium">{request.to.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        <span>{request.items.length} items</span>
                      </div>
                      {userRole === 'manager' && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          <span>SAR {request.estimatedCost.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{request.description}</p>
                    {request.specialRequirements && (
                      <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {request.specialRequirements}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedRequest(request.id === selectedRequest ? null : request.id)}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedRequest === request.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-gray-200 space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Requested By</p>
                      <p className="font-medium text-gray-900">{request.requestedBy}</p>
                      <p className="text-xs text-gray-500 mt-1">{request.requestedDate}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Required Date</p>
                      <p className="font-medium text-gray-900">{request.requiredDate}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Total Weight</p>
                      <p className="font-medium text-gray-900">{request.totalWeight.toLocaleString()} kg</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Total Volume</p>
                      <p className="font-medium text-gray-900">{request.totalVolume} m³</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Items</h4>
                    <div className="space-y-2">
                      {request.items.map((item, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                            {item.temperature && (
                              <p className="text-xs text-blue-600 mt-1">Temperature: {item.temperature}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">Qty: {item.quantity}</p>
                            <p className="text-sm text-gray-600">{item.weight} kg</p>
                            {userRole === 'driver' && request.status === 'in-progress' && (
                              <button
                                onClick={() => handleScanItem(request.id, item.sku)}
                                className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex items-center gap-1"
                              >
                                <QrCode className="w-3 h-3" />
                                Scan
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Manager View - Approval */}
                  {userRole === 'manager' && request.requiresApproval && request.approvalStatus === 'pending' && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-yellow-600" />
                          <span className="font-medium text-yellow-900">Approval Required</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(request.id)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(request.id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Manager View - Assignment */}
                  {userRole === 'manager' && request.status === 'approved' && !request.assignedVehicle && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAssign(request.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        Assign Vehicle & Driver
                      </button>
                    </div>
                  )}

                  {/* Assigned Vehicle Info */}
                  {request.assignedVehicle && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Assigned Vehicle</p>
                          <p className="font-medium text-gray-900">{request.assignedVehicle}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Assigned Driver</p>
                          <p className="font-medium text-gray-900">{request.assignedDriver}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Driver Actions */}
                  {userRole === 'driver' && request.status === 'in-progress' && (
                    <div className="space-y-3">
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h5 className="font-semibold text-gray-900 mb-2">Route Information</h5>
                        <div className="flex items-center gap-2 text-sm">
                          <Navigation className="w-4 h-4 text-blue-600" />
                          <span>{request.from.location} → {request.to.location}</span>
                        </div>
                        {request.startedAt && (
                          <p className="text-xs text-gray-600 mt-2">Started: {request.startedAt}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleCaptureEPOD(request.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center gap-2"
                        >
                          <Camera className="w-4 h-4" />
                          Capture ePOD
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(request.id, 'completed')}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Mark as Completed
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                {userRole === 'manager' && request.status === 'approved' && !request.assignedVehicle && (
                  <button
                    onClick={() => handleAssign(request.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Assign Vehicle
                  </button>
                )}
                {(userRole === 'user' || userRole === 'manager') && request.status === 'in-progress' && (
                  <button
                    onClick={() => alert(`Opening live tracking for ${request.id}. Navigate to the "Live Tracking" tab to view real-time location.`)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                  >
                    Track Shipment
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Create Request Modal */}
      {showCreateModal && (userRole === 'user' || userRole === 'manager') && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Create Logistics Request</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <XCircle className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Request Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {requestTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 text-left"
                        >
                          <Icon className="w-5 h-5 text-gray-600 mb-2" />
                          <p className="font-medium text-gray-900 text-sm">{type.label}</p>
                          <p className="text-xs text-gray-600">{type.category}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Location</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="Select origin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To Location</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="Select destination"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="emergency">Emergency/Hot</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Required Date</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Describe the logistics request (equipment, items, finished products, etc.)..."
                  />
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    alert('Request created successfully! In production, this would create the request and trigger approval workflow if needed.');
                    setShowCreateModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Request
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LogisticsRequests;
